import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ISSUE_TYPES = ["location", "country", "transcript", "summary", "description", "not_travel", "other"] as const;
type IssueType = (typeof ISSUE_TYPES)[number];

export function ReportVideoDialog({ videoId, trigger }: { videoId: string; trigger: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [issueType, setIssueType] = useState<IssueType>("location");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    const { error } = await supabase.from("video_reports").insert({
      video_id: videoId,
      issue_type: issueType,
      message: message.trim(),
      reporter_email: email.trim() || null,
    });
    setSending(false);
    if (error) {
      toast.error(t("report.error"));
      return;
    }
    toast.success(t("report.success"));
    setMessage("");
    setEmail("");
    setIssueType("location");
    setOpen(false);
  }

  const inputCls =
    "w-full bg-paper ring-hairline px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber/50";
  const labelCls = "text-mono-caps text-[10px] text-ink/50 mb-1 block";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-paper max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-lg">{t("report.title")}</DialogTitle>
          <DialogDescription className="text-sm text-ink/60">{t("report.desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className={labelCls}>{t("report.type")}</label>
            <select aria-label={t("report.typeLabel")} value={issueType} onChange={(e) => setIssueType(e.target.value as IssueType)} className={inputCls}>
              {ISSUE_TYPES.map((k) => (
                <option key={k} value={k}>
                  {t(`report.types.${k}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t("report.message")} *</label>
            <textarea
              required
              rows={4}
              maxLength={4000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("report.messagePlaceholder")}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>
              {t("report.email")} <span className="lowercase">({t("report.optional")})</span>
            </label>
            <input type="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-ink text-paper text-sm font-medium px-3.5 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            {sending ? t("report.sending") : t("report.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
